import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import NotificationMgmtService from '../services/NotificationMgmtService';
import {
  Button,
  Grid,
  TextField,
  Paper,
  Theme,
  WithStyles,
  withStyles,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Typography,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import HttpCallConfig from '../../core/models/HttpCallConfig';
import AreYouSureModal from 'src/modules/shared/dataSharing/AreYouSureModal';
import Notification from '../models/Notification';
import Loading from 'src/modules/shared/components/loading/Loading';
import StateHelper from 'src/modules/shared/helpers/StateHelper';
import NotificationChannel from 'src/modules/organization/models/NotificationChannel';
import NotificationChannelMgmtService from 'src/modules/shared/services/NotificationChannelMgmtService';
import NotificationType from '../models/NotificationType';

const styles = (theme: Theme) => ({
  paper: {
    padding: theme.spacing.unit * 3,
  },
  button: {
    marginTop: theme.spacing.unit * 1,
  },
  dropDown: {
    width: 108,
  },
  textField: {
    width: 240,
  },
});

interface IValidation {
  'notification.name'?: string;
  'notification.channelId'?: string;
}

interface IState {
  notification: Notification;
  notificationChannels: NotificationChannel[];
  notificationTypes: NotificationType[];
  saving: boolean;
  deleteNotification: boolean;
  errors: IValidation;
  loading: boolean;
}

interface IProps extends WithStyles<typeof styles>, RouteComponentProps<any> {}

class NotificationDetail extends React.Component<IProps> {
  public state: IState = {
    notification: new Notification(),
    notificationChannels: [],
    notificationTypes: [],
    saving: false,
    deleteNotification: false,
    errors: {},
    loading: true,
  };

  get notificationId() {
    return this.props.match.params.notificationId;
  }

  get overviewPath() {
    return `/subscriptions/${this.props.match.params.assetId}/${this.props.match.params.environment}/notifications`;
  }

  public async componentDidMount() {
    const notificationDataPromises = [];

    notificationDataPromises.push(
      NotificationChannelMgmtService.GetNotificationChannels()
    );

    notificationDataPromises.push(
      NotificationMgmtService.GetNotificationTypesForAsset(this.props.match.params.assetId)
    );

    if (this.notificationId) {
      notificationDataPromises.push(
        NotificationMgmtService.GetNotificationById(this.notificationId)
      );
    }

    const notificationData = await Promise.all(notificationDataPromises);

    this.setState({ notificationChannels: notificationData[0] });
    this.setState({ notificationTypes: notificationData[1] });

    if (this.notificationId) {
      this.setState({ notification: notificationData[2] });
    }

    this.setState({ loading: false });
  }

  public deleteNotification = async () => {
    const { history } = this.props;

    try {
      this.setState({ saving: true, deleteNotification: false });

      const httpCallConfig = new HttpCallConfig();
      httpCallConfig.successMessage = 'Notification Channel deleted';
      await NotificationMgmtService.DeleteNotification(
        this.notificationId,
        httpCallConfig
      );

      history.push(this.overviewPath);
    } catch {
      this.setState({ saving: false });
    }
  };

  public saveNotification = async () => {
    const { history } = this.props;
    const { notification } = this.state;

    try {
      const errors = this.validateState();
      if (Object.keys(errors).length) {
        return this.setState({ errors });
      }

      this.setState({ saving: true });

      if (this.notificationId) {
        const httpCallConfig = new HttpCallConfig();
        httpCallConfig.successMessage = 'Notification updated succesfully';
        await NotificationMgmtService.PutNotification(
          this.notificationId,
          notification,
          httpCallConfig
        );
      } else {
        const httpCallConfig = new HttpCallConfig();
        httpCallConfig.successMessage = 'Notification added succesfully.';
        await NotificationMgmtService.PostNotification(
          this.props.match.params.assetId,
          notification,
          httpCallConfig
        );
      }

      history.push(this.overviewPath);
    } catch {
      this.setState({ saving: false });
    }
  };

  public getOnChange = (path: string) => {
    return (event: React.ChangeEvent<any>) => {
      const value =
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.value;
      this.setState(state => StateHelper.ImmutableChange(state, path, value));
    };
  };

  public onTypeFilterToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const typeFilterOn = e.target.checked;

    this.setState(state =>
      StateHelper.ImmutableChange(
        state,
        'notification.type',
        typeFilterOn ? this.state.notificationTypes[0] : null
      )
    );
  };

  public validateState = () => {
    const { notification } = this.state;
    const errors: IValidation = {};

    if (!notification.name) {
      errors['notification.name'] = 'Name is required';
    }

    if (!notification.channelId) {
      errors['notification.channelId'] = 'Channel is required';
    }

    return errors;
  };

  public render() {
    const { classes, history } = this.props;
    const {
      notification,
      notificationChannels,
      saving,
      deleteNotification,
      errors,
      loading,
    } = this.state;

    if (loading) {
      return (
        <div>
          <Loading />
        </div>
      );
    }

    return (
      <Paper className={classes.paper}>
        <Grid container={true} spacing={24}>
          <Grid item={true} style={{ flex: 1 }}>
            <Typography variant="title">
              {this.notificationId ? 'Edit' : 'Add'} Notification
            </Typography>
          </Grid>
          {this.notificationId && (
            <Grid item={true}>
              <Button
                size="small"
                onClick={() => this.setState({ deleteNotification: true })}
                disabled={saving}
              >
                Remove Notification
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <TextField
              name="notification.name"
              label="Name"
              value={notification.name}
              margin="normal"
              className={classes.textField}
              required={true}
              onChange={this.getOnChange('notification.name')}
              error={!!errors['notification.name']}
              helperText={errors['notification.name']}
            />
          </Grid>
          <Grid item={true} style={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={notification.type !== null}
                  color="secondary"
                  name="notification.type"
                  onChange={this.onTypeFilterToggle}
                />
              }
              label="Filter notification type"
            />
          </Grid>
        </Grid>
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <FormControl className={classes.textField}>
              <InputLabel
                id="notification.channelId"
                required
                error={!!errors['notification.channelId']}
              >
                Notification Channel
              </InputLabel>
              <Select
                id="notification.channelId"
                value={notification.channelId}
                onChange={this.getOnChange('notification.channelId')}
                error={!!errors['notification.channelId']}
              >
                {notificationChannels.map(channel => (
                  <MenuItem key={channel.id} value={channel.id}>
                    {channel.name} - {channel.channelTypeId}
                  </MenuItem>
                ))}
              </Select>
              {errors['notification.channelId'] ? (
                <FormHelperText error>
                  {errors['notification.channelId']}
                </FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
          {notification.type && (
            <Grid item={true}>
              <FormControl className={classes.textField}>
                <InputLabel id="notification.type.type" required>
                  Notification Type
                </InputLabel>
                <Select
                  id="notification.type.type"
                  value={notification.type.type}
                  onChange={this.getOnChange('notification.type.type')}
                >
                  {this.state.notificationTypes.map(type => (
                    <MenuItem key={type.type} value={type.type}>
                      {type.type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <Button
              variant="outlined"
              color="secondary"
              className={classes.button}
              onClick={history.goBack}
            >
              BACK
            </Button>
          </Grid>
          <Grid item={true}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.button}
              onClick={this.saveNotification}
              disabled={saving}
            >
              SAVE
            </Button>
          </Grid>
        </Grid>
        <AreYouSureModal
          onClose={() => this.setState({ deleteNotification: false })}
          onConfirm={this.deleteNotification}
          message="Are you sure you wish to remove this notification?"
          open={deleteNotification}
        />
      </Paper>
    );
  }
}

export default withRouter(withStyles(styles)(NotificationDetail));



// WEBPACK FOOTER //
// ./src/modules/consumer/components/NotificationDetail.tsx